import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, MapPin, Clock, CheckCircle2, XCircle, Hotel, Utensils, Bus, Camera } from "lucide-react";
import DestinationCard from "@/components/site/DestinationCard";
import FareCalendar from "@/components/site/FareCalendar";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { useCurrency } from "@/context/CurrencyContext";

export default function PackageDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/packages/${slug}`).then((r) => { setData(r.data); setActiveImg(0); });
  }, [slug]);

  if (!data) return <div className="pt-32 min-h-screen text-center text-slate-500">Loading...</div>;
  const p = data.package;

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-sm text-slate-500 mb-4"><Link to="/packages" className="hover:text-[#D4AF37]">Packages</Link> / <span className="text-blue-950 dark:text-white">{p.title}</span></div>
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Gallery + Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={p.gallery[activeImg] || p.image} alt={p.title} className="w-full h-[480px] object-cover"/>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {p.gallery.map((g, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={()=>setActiveImg(i)}
                  className={`rounded-xl overflow-hidden h-28 border-2 ${activeImg === i ? "border-[#D4AF37]" : "border-transparent"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>

            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">{p.category}</div>
                  <h1 data-testid="package-title" className="text-4xl font-bold text-blue-950 dark:text-white mt-2">{p.title}</h1>
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-600 dark:text-slate-300">
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/>{p.location}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4"/>{p.duration}</span>
                    <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]"/>{p.rating} ({p.reviews_count})</span>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-slate-600 dark:text-slate-300 leading-relaxed">{p.description}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                {p.hotel && <span className="px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-white/10 flex items-center gap-1"><Hotel className="w-3 h-3"/>Hotel</span>}
                {p.meals && <span className="px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-white/10 flex items-center gap-1"><Utensils className="w-3 h-3"/>Meals</span>}
                {p.transport && <span className="px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-white/10 flex items-center gap-1"><Bus className="w-3 h-3"/>Transport</span>}
                {p.sightseeing && <span className="px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-white/10 flex items-center gap-1"><Camera className="w-3 h-3"/>Sightseeing</span>}
              </div>
            </div>

            {/* Itinerary */}
            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <h2 className="text-2xl font-bold text-blue-950 dark:text-white">Day-by-Day Itinerary</h2>
              <div className="mt-6 space-y-4">
                {p.itinerary.map((d) => (
                  <div key={d.day} className="relative pl-10">
                    <div className="absolute left-0 top-0 w-8 h-8 rounded-full btn-gold flex items-center justify-center text-xs font-bold">{d.day}</div>
                    <div className="text-lg font-semibold text-blue-950 dark:text-white">{d.title}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{d.details}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
                <h3 className="text-xl font-bold text-emerald-700 dark:text-emerald-300">Inclusions</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {p.inclusions.map((i,idx) => <li key={idx} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5"/>{i}</li>)}
                </ul>
              </div>
              <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
                <h3 className="text-xl font-bold text-rose-700 dark:text-rose-300">Exclusions</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {p.exclusions.map((i,idx) => <li key={idx} className="flex gap-2"><XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5"/>{i}</li>)}
                </ul>
              </div>
            </div>

            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
              <h3 className="text-xl font-bold text-blue-950 dark:text-white">Hotel & Pickup</h3>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{p.hotel_details}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.pickup_points.map(pt => <span key={pt} className="px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-white/10">{pt}</span>)}
              </div>
            </div>

            {/* Fare Calendar */}
            <FareCalendar slug={p.slug} />
          </div>

          {/* Booking Sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit">
            <div className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 shadow-2xl">
              <div className="text-xs uppercase tracking-widest text-slate-500">Starting from</div>
              <div className="text-4xl font-bold text-blue-950 dark:text-white">{format(p.price)}</div>
              <div className="text-xs text-slate-500">/person · {p.duration}</div>
              <button data-testid="detail-book-now" onClick={() => nav(`/book/${p.slug}`)} className="mt-6 w-full py-3.5 rounded-full btn-gold text-sm">Book Now</button>
              <a href="https://wa.me/919891782652" target="_blank" rel="noreferrer" className="mt-3 block text-center py-3.5 rounded-full btn-outline-gold text-sm">WhatsApp Us</a>
              <div className="mt-6 text-xs text-slate-500">✦ 24×7 concierge · Free date change · Best price guarantee</div>
            </div>
          </aside>
        </div>

        {data.related?.length > 0 && (
          <section className="mt-20 mb-24 lg:mb-0">
            <h2 className="text-3xl font-bold text-blue-950 dark:text-white mb-8">You may also love</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {data.related.map(r => <DestinationCard key={r.slug} p={r}/>)}
            </div>
          </section>
        )}
      </div>

      {/* Sticky mobile booking bar */}
      <div data-testid="sticky-book-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-slate-200 dark:border-white/10 px-4 py-3 flex items-center gap-3 shadow-2xl">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Starts at</div>
          <div className="text-lg font-bold text-blue-950 dark:text-white leading-tight">{format(p.price)}<span className="text-[10px] font-normal text-slate-500"> /person</span></div>
        </div>
        <a href="https://wa.me/919891782652" target="_blank" rel="noreferrer"
          data-testid="sticky-whatsapp"
          className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0" aria-label="WhatsApp">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M17.5 14.4c-.3-.2-1.8-.9-2.1-1s-.5-.1-.7.2c-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-1.8-.9-3-1.6-4.2-3.6-.3-.5.3-.5.9-1.6.1-.2 0-.4 0-.5s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5-.2 0-.4 0-.6 0-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2.1 3.2 5 4.4.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z"/><path d="M12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5C8.3 21.5 10.1 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.7 0-3.4-.5-4.8-1.4l-.3-.2-3.4 1 1.1-3.3-.2-.4C3.4 14.5 3 13.3 3 12c0-4.9 4-9 9-9s9 4 9 9-4 9.2-9 9.2z"/></svg>
        </a>
        <button onClick={() => nav(`/book/${p.slug}`)} data-testid="sticky-book-now"
          className="flex-1 max-w-[180px] py-3 rounded-full btn-gold text-sm font-bold">Book Now</button>
      </div>
    </div>
  );
}
